<template>
  <div class="mx-5">
    <v-row>
      <v-col cols="12" class="d-flex flex-wrap align-center mb-2 pb-0">
        <v-card-title style="font-weight: 1000; text-transform: uppercase" class="mb-0 pb-0 px-0 text-h5">Check-Ins</v-card-title>
        <v-spacer />
        <v-text-field
          v-model="search"
          label="Suchen..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          single-line
          class="mr-4"
          style="max-width: 300px" />
        <v-switch v-model="filterCheckedoutMembers" label="Nur Eingecheckte" hide-details inset color="success" class="mr-2 flex-grow-0" />
        <v-btn icon @click="fetchCheckinPool(true)" :loading="fetchingData">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="isAppleDevice && !initialLoadDone && !fetchingData">
      <v-col cols="12" class="text-center py-10">
        <v-card outlined class="pa-6 rounded-xl mx-auto" max-width="500">
          <v-icon size="64" color="primary">mdi-apple-safari</v-icon>
          <h3 class="text-h6 mt-4">Check-Ins laden</h3>
          <p class="grey--text">Um die Performance auf diesem Gerät zu optimieren, werden Check-Ins erst auf Knopfdruck geladen.</p>
          <v-btn x-large color="primary" rounded @click="fetchCheckinPool(true)"> <v-icon left>mdi-play</v-icon> Starten </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <template v-else>
      <v-row v-if="initialLoadDone && pool.length > 0" class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="fill-height rounded-xl" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
              <span class="subtitle">Heutige Auslastung</span>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center">
                <div class="text-center mr-4">
                  <div class="text-h3 font-weight-bold">{{ liveStats.currentlyCheckedIn }}</div>
                  <div class="text-caption">Jetzt im Studio</div>
                </div>
                <div class="flex-grow-1">
                  <apexchart v-if="chartData" type="bar" height="150" :options="occupancyChartOptions" :series="occupancyChartSeries"></apexchart>
                  <div v-else class="d-flex justify-center align-center" style="height: 100px">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="fill-height rounded-xl" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-cake-variant</v-icon>
              <span class="subtitle">Altersverteilung (Eingecheckt)</span>
            </v-card-title>
            <v-card-text>
              <div v-for="(count, group) in liveStats.ageDistribution" :key="group" class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ group }}</span>
                <v-chip size="small" color="blue-lighten-4" text-color="primary" class="font-weight-bold">{{ count }}</v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="fetchingData && !pool.length">
        <v-col v-for="n in 6" :key="n" cols="12" md="6" lg="4">
          <v-skeleton-loader type="card-avatar, article, actions"></v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row v-else-if="initialLoadDone && !pool.length">
        <v-col cols="12" class="text-center grey--text mt-10">
          <v-icon size="48" class="mb-2">mdi-account-off-outline</v-icon>
          <p>Keine Mitglieder für die aktuelle Auswahl gefunden.</p>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col style="margin-top: 80px" v-for="entry in processedPool" :key="entry._id" cols="12" md="6" lg="4" xl="3">
          <v-card
            v-intersect.quiet="{
              handler: (entries, observer, isIntersecting) => onIntersect(entry._id, isIntersecting),
              options: { threshold: [0.1] },
            }"
            class="card-transition fill-height rounded-xl position-relative"
            :elevation="entry.isCheckedIn ? 8 : 2"
            :class="{ 'checked-in-border': entry.isCheckedIn }"
            @click="selectEntry(entry)">
            <div class="d-flex justify-center">
              <v-avatar class="avatar-shadow" style="transform: translate(0px, -50%); position: absolute" size="130">
                <v-img
                  v-if="visibleItemsMap[entry._id]"
                  :src="entry.profilePicture"
                  :lazy-src="placeholderImage"
                  :alt="`${entry.firstName} ${entry.lastName}`">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-icon v-else size="64" color="grey lighten-3">mdi-account</v-icon>
              </v-avatar>
            </div>

            <div class="d-flex justify-space-between pa-0">
              <v-chip small style="font-weight: 1000" class="mx-2 mt-3" color="success" label>
                <v-icon>mdi-login</v-icon> {{ entry.checkinTime }}
              </v-chip>
              <v-chip small style="font-weight: 1000" v-if="entry.checkoutTime" class="mx-2 mt-3" color="warning" label>
                <v-icon>mdi-logout</v-icon>{{ entry.checkoutTime }}
              </v-chip>
            </div>

            <div class="d-flex align-center px-4 pb-4 mt-8 pt-3">
              <div class="flex-grow-1">
                <div class="text-h6 font-weight-bold d-flex justify-center align-center text-center">{{ entry.firstName }} {{ entry.lastName }}</div>
                <div v-if="entry.isBday" class="d-flex justify-center my-2">
                  <v-chip class="birthday-pulse" color="amber" dark small> <v-icon left small>mdi-cake-variant</v-icon> Happy Birthday! </v-chip>
                </div>
                <div class="text-caption grey--text d-flex flex-column justify-center align-center">
                  <span>{{ entry.birthdayFormatted }} ({{ entry.age }})</span>
                  <span v-if="entry.email"> {{ entry.email }}</span>
                </div>
              </div>
            </div>

            <div class="pa-4 pt-0">
              <div v-if="entry.mainContract" class="subtitle mb-1">Mitgliedschaft</div>
              <div v-if="entry.mainContract" class="mb-3">
                <div class="d-flex align-center justify-space-between text-body-2">
                  <div>
                    <v-icon small class="mr-1">mdi-card-account-details-outline</v-icon>
                    <span>{{ entry.mainContract.NAME }}</span>
                  </div>
                  <span class="text-caption grey--text">bis {{ entry.contractEndDate }}</span>
                </div>
                <div v-if="entry.packages && entry.packages.length" class="mt-2">
                  <v-chip
                    color="primary"
                    v-for="pkg in entry.packages"
                    :key="pkg.CONTRACTID"
                    class="mr-1 mb-1 subtitle"
                    style="font-size: 0.7rem !important"
                    small
                    label>
                    {{ pkg.NAME }}
                  </v-chip>
                </div>
              </div>

              <v-divider />

              <div class="my-3">
                <div class="subtitle mb-1 d-flex justify-space-between align-center">
                  Interaktionen
                  <v-chip
                    @click.stop="selectEntryInteractions(entry)"
                    style="font-size: 0.7rem"
                    :color="entry.interactionInfo.status.color"
                    size="x-small"
                    label>
                    {{ entry.interactionInfo.status.text }}
                  </v-chip>
                </div>
                <div v-if="entry.interactionInfo.total > 0">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon :color="entry.interactionInfo.status.color" class="mr-2">mdi-history</v-icon>
                      <span class="text-body-2 font-weight-bold mr-2">{{ entry.interactionInfo.total }} gesamt</span>
                      <span class="text-body-2">(letzte {{ entry.interactionInfo.timeAgo }})</span>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <span class="text-body-2">Bisher keine Interaktionen erfasst.</span>
                </div>
              </div>

              <v-row>
                <v-col cols="12" sm="6">
                  <div class="subtitle mb-1">Trainingsplan</div>
                  <div class="text-body-2">
                    <div v-if="entry.workoutInfo.newDoc" class="d-flex align-center">
                      <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
                      <span>Neu: {{ entry.workoutInfo.newDoc }}</span>
                    </div>
                    <div v-if="entry.workoutInfo.oldDoc" class="d-flex align-center mt-1">
                      <v-icon small class="mr-2" color="warning">mdi-alert-circle-outline</v-icon>
                      <span>V1: {{ entry.workoutInfo.oldDoc }}</span>
                    </div>
                    <div v-if="!entry.workoutInfo.newDoc && !entry.workoutInfo.oldDoc" class="d-flex align-center">
                      <v-icon small class="mr-2" color="error">mdi-close-circle</v-icon>
                      <span>Kein Plan</span>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="subtitle mb-1">Anamnese</div>
                  <div class="text-body-2">
                    <div v-if="entry.healthInfo.newDoc" class="d-flex align-center">
                      <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
                      <span>Neu: {{ entry.healthInfo.newDoc }}</span>
                    </div>
                    <div v-if="entry.healthInfo.oldDoc" class="d-flex align-center mt-1">
                      <v-icon small class="mr-2" color="warning">mdi-alert-circle-outline</v-icon>
                      <span>Alt: {{ entry.healthInfo.oldDoc }}</span>
                    </div>
                    <div v-if="!entry.healthInfo.newDoc && !entry.healthInfo.oldDoc" class="d-flex align-center">
                      <v-icon small class="mr-2" color="error">mdi-close-circle</v-icon>
                      <span>Keine Daten</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
            <div v-if="!entry.user" class="no-account-overlay d-flex align-center justify-center">
              <v-icon color="white" class="mr-2">mdi-account-alert-outline</v-icon>
              <span class="font-weight-bold">Kein roody Account verknüpft</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="initialLoadDone && !fetchingData && pool.length < totalEntries" class="mt-6">
        <v-col cols="12" class="text-center">
          <v-btn color="primary" variant="elevated" :loading="fetchingData" @click="loadMoreClick"> Lade weitere Checkins </v-btn>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="detailsDialog" max-width="700px">
      <v-card v-if="selectedEntry">
        <v-card-title class="d-flex align-center">
          <span class="text-h5">Interaktionsverlauf</span>
          <v-spacer />
          <span class="font-weight-regular text-body-1">{{ selectedEntry.firstName }} {{ selectedEntry.lastName }}</span>
        </v-card-title>
        <v-card-text>
          <v-timeline v-if="selectedEntry.interactionInfo.total > 0" align="start" side="end" density="compact">
            <v-timeline-item v-for="interaction in selectedEntry.interactionInfo.sorted" :key="interaction._id" dot-color="primary" size="small">
              <div class="d-flex justify-space-between">
                <div class="font-weight-bold">{{ interaction.interactions.reason }}</div>
                <div class="text-caption">{{ formatInteractionDate(interaction.interactions.date || interaction.createdAt) }}</div>
              </div>
              <div>von {{ interaction.employee.vorname }} {{ interaction.employee.nachname }}</div>
              <div class="text-caption text--secondary">Ort: {{ interaction.interactions.location }}</div>
              <div v-if="interaction.interactions.notes" class="text-body-2 mt-2 pa-2 grey lighten-4 rounded">
                {{ interaction.interactions.notes }}
              </div>
            </v-timeline-item>
          </v-timeline>
          <div v-else class="text-center grey--text py-5">
            <p>Keine Interaktionen vorhanden.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "NewCheckinPool",

  data() {
    return {
      pool: [],
      selectedEntry: null,
      fetchingData: false,
      filterCheckedoutMembers: true,
      search: "",
      detailsDialog: false,
      placeholderImage: "",
      initialLoadDone: false,
      page: 1,
      itemsPerPage: 12,
      totalEntries: 0,
      visibleItemsMap: {}, // NEU: Steuert die Sichtbarkeit der Bilder pro Eintrag
      liveStats: {
        currentlyCheckedIn: 0,
        ageDistribution: {},
      },
      chartData: null,
    }
  },

  computed: {
    isAppleDevice() {
      // Erkennt iPads und iPhones anhand des UserAgents
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    },

    occupancyChartSeries() {
      if (!this.chartData) return [{ name: "Mitglieder", data: [] }]
      return [{ name: "Mitglieder", data: this.chartData.occupancyData.data }]
    },

    occupancyChartOptions() {
      if (!this.chartData) return { xaxis: { categories: [] } }
      return {
        chart: { type: "bar", height: 100, sparkline: { enabled: true }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: "80%", borderRadius: 2, distributed: false } },
        tooltip: { y: { formatter: (val) => `${val} Check-Ins` }, x: { show: true } },
        yaxis: { show: true },
        grid: { show: false },
        legend: { show: false },
        xaxis: { categories: this.chartData.occupancyData.labels },
      }
    },

    processedPool() {
      if (!this.pool) return []
      return this.pool.map((entry) => {
        const isCheckedIn = !entry.checkoutDate || this.$moment(entry.checkoutDate).isBefore(entry.checkinDate)
        const getInteractionInfo = (interactions) => {
          if (!interactions || interactions.length === 0) {
            return { total: 0, timeAgo: "Nie", status: { color: "error", text: "Keine" }, sorted: [] }
          }
          const sorted = [...interactions].sort(
            (a, b) => this.$moment(b.interactions?.date || b.createdAt).valueOf() - this.$moment(a.interactions?.date || a.createdAt).valueOf(),
          )
          const lastDate = this.$moment(sorted[0].interactions?.date || sorted[0].createdAt)
          const daysAgo = this.$moment().diff(lastDate, "days")
          let status
          if (daysAgo <= 14) status = { color: "success", text: "Aktuell" }
          else if (daysAgo <= 45) status = { color: "warning", text: "Bald wieder" }
          else status = { color: "error", text: "Überfällig" }
          return { total: interactions.length, timeAgo: lastDate.fromNow(), status, sorted }
        }
        return {
          ...entry,
          isCheckedIn,
          profilePicture: this.getPicture(entry),
          age: entry.birthday ? `${this.$moment().diff(this.$moment(entry.birthday), "years")} Jahre` : "Unbekannt",
          birthdayFormatted: entry.birthday ? this.$moment(entry.birthday).format("DD.MM.YYYY") : "Unbekannt",
          isBday: entry.birthday ? this.$moment(entry.birthday).format("MM-DD") === this.$moment().format("MM-DD") : false,
          checkinTime: this.$moment(entry.checkinDate).format("HH:mm"),
          checkoutTime: entry.checkoutDate ? this.$moment(entry.checkoutDate).format("HH:mm") : "",
          lastCourse: entry?.courses && entry?.courses[0] ? entry?.courses[0] : null,
          contractEndDate: entry.mainContract ? this.$moment(entry.mainContract.TERM_ENDDATE).format("DD.MM.YYYY") : "N/A",
          workoutInfo: this.getDocumentStatus(entry, ["workoutsV2", "workoutV2"], ["workouts"]),
          healthInfo: this.getDocumentStatus(entry, ["documents.medicalHistory"], ["documents.anamnese"]),
          interactionInfo: getInteractionInfo(entry.customerInteractions),
        }
      })
    },
  },

  watch: {
    search() {
      this.handleQueryChange()
    },
    filterCheckedoutMembers() {
      this.handleQueryChange()
    },
  },

  created() {
    this.searchDebounce = null
    // Automatisches Laden nur, wenn es kein Apple-Gerät ist
    if (!this.isAppleDevice) {
      this.fetchCheckinPool(true)
    }
  },

  methods: {
    // Handler für Intersection Observer: Bild erst anzeigen, wenn Karte sichtbar wird
    onIntersect(id, isIntersecting) {
      if (isIntersecting && !this.visibleItemsMap[id]) {
        this.$set(this.visibleItemsMap, id, true)
      }
    },

    async fetchCheckinPool(isNewQuery = false) {
      if (this.fetchingData) return
      this.fetchingData = true

      if (isNewQuery) {
        this.page = 1
        this.pool = []
        this.initialLoadDone = false
        this.chartData = null
        this.visibleItemsMap = {} // Reset der Sichtbarkeit
      }

      try {
        const params = {
          page: this.page,
          limit: this.itemsPerPage,
          search: this.search,
          filterCheckedout: this.filterCheckedoutMembers,
        }
        const { entries, total, stats } = await api.fetchCheckinPoolNew(params)

        this.pool.push(...entries)
        this.totalEntries = total

        if (isNewQuery && stats) {
          this.liveStats = stats.liveStats
          this.chartData = stats
        }
      } catch (e) {
        console.error("Fehler beim Laden der Check-Ins:", e)
      } finally {
        this.fetchingData = false
        this.$nextTick(() => {
          this.initialLoadDone = true
        })
      }
    },

    loadMore() {
      if (this.pool.length >= this.totalEntries) return
      this.page++
      this.fetchCheckinPool()
    },

    loadMoreClick() {
      if (!this.fetchingData && this.initialLoadDone) this.loadMore()
    },

    handleQueryChange() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.fetchCheckinPool(true)
      }, 300)
    },

    selectEntry(entry) {
      if (!entry || !entry.user || !entry.user._id) {
        this.$toast.error("Kein Benutzer verknüpft.")
        return
      }
      this.$emit("selectCustomerFromPool", entry)
    },

    selectEntryInteractions(entry) {
      this.selectedEntry = entry
      this.detailsDialog = true
    },

    formatInteractionDate(dateString) {
      return this.$moment(dateString).format("DD.MM.YYYY, HH:mm")
    },

    getPicture(entry) {
      if (entry.user?.bild) {
        const bildMitWebp = entry.user.bild.replace(/\.[^/.]+$/, ".webp")
        return `https://app.wellcomefit.ch/img/profilbilder/${bildMitWebp}`
      } else if (entry.user?.tac_image) {
        return `data:image/jpeg;base64,${entry.user.tac_image}`
      }
      return this.placeholderImage
    },

    getDocumentStatus(entry, newKeys, oldKeys) {
      const findLatestDate = (keys) => {
        for (const key of keys) {
          const getValue = (obj, path) => path.split(".").reduce((o, i) => (o ? o[i] : undefined), obj)
          let docs = getValue(entry, key)
          if (docs && !Array.isArray(docs)) docs = [docs]
          if (Array.isArray(docs) && docs.length > 0) {
            const createMoment = (obj) => {
              if (obj.createdAt) return this.$moment(obj.createdAt)
              if (obj.date) return this.$moment.unix(obj.date)
              if (obj.updatedAt) return this.$moment(obj.updatedAt)
              if (obj.ts) return this.$moment.unix(obj.ts)
              if (obj.latest?.ts) return this.$moment.unix(obj.latest.ts)
              return null
            }
            const latest = [...docs].sort((a, b) => {
              const momentA = createMoment(a)
              const momentB = createMoment(b)
              return (momentB ? momentB.valueOf() : 0) - (momentA ? momentA.valueOf() : 0)
            })[0]
            const latestMoment = createMoment(latest)
            return latestMoment ? latestMoment.format("DD.MM.YYYY") : "Kein Datum"
          }
        }
        return null
      }
      return { newDoc: findLatestDate(newKeys), oldDoc: findLatestDate(oldKeys) }
    },
  },
}
</script>

<style scoped>
.subtitle {
  font-weight: 1000;
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #555;
}
.card-transition {
  background: #ffffff;
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  cursor: pointer;
  border: 1px solid #e0e0e0;
}

.card-transition:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
}

.checked-in-border {
  border-left: 6px solid #4caf50;
  box-shadow: -5px 0 20px -8px #4caf50;
}

.avatar-shadow {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.no-account-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background-color: rgba(33, 33, 33, 0.85);
  color: white;
  z-index: 10;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 179, 0, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 10px 15px rgba(255, 179, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 179, 0, 0);
  }
}

.birthday-pulse {
  animation: pulse 2s infinite;
}
</style>
